import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { getPatientPrescription } from "../../SalesService";

export const usePrescriptionManager = ({ t, loadMedicines, upsertCartItem, setLoading, isHospitalConnected, customerType,
	pharmacyPOS, stateCustomerType }) => {
	const [recentPrescriptions, setRecentPrescriptions] = useState([]);
	const [prescriptionDetails, setPrescriptionDetails] = useState([]);
	const [showPrescriptionsCard, setShowPrescriptionsCard] = useState(false);
	const [showPrescriptionOffcanvas, setShowPrescriptionOffcanvas] = useState(false);
	const [selectedPrescription, setSelectedPrescription] = useState(null);
	const [consultationOpen, setConsultationOpen] = useState(false);
	const [addedPrescriptions, setAddedPrescriptions] = useState(new Set());


	const loadRecentPrescriptions = useCallback(
		async (patientId) => {
			if (!patientId) return;
			// console.log(customerType);

			setLoading(true);
			try {
				const body = {
					patient_id: patientId,
					isHospitalPatient: isHospitalConnected && customerType === "hospital_patient",
					pageNo: 1,
					pageSize: 5,
				};
				// Only add clinic_id if hospital connected AND customer type is hospital_patient
				if (isHospitalConnected && customerType === "hospital_patient") {
					body.clinic_id = pharmacyPOS.hospital_id;
					// console.log("🏥 Loading hospital prescriptions for patient:", patientId);
				} else {
					// console.log("🏪 Loading pharmacy prescriptions for patient:", patientId);
				}
				const response = await getPatientPrescription(body);
				const data = response.data?.data || {};
				let prescriptions = [];
				if (isHospitalConnected && customerType === "hospital_patient") {
					prescriptions = data[0].prescriptions || [];
					setPrescriptionDetails(data[0])
				} else {
					// Map lastFewSales into unified shape for reuse in UI
					prescriptions = (data.lastFewSales || []).map((sale) => ({
						_id: sale._id,
						createdAt: sale.createdAt,
						doctor: { name: "N/A" }, // No doctor data for non-hospital case
						prescriptions: sale.items?.map((item) => ({
							medication_name: item.medicineName,
							salt_composition: item.brandName,
							dosage_name: item.productForm,
							duration: "N/A",
							type: "Sale",
							qty: item.medicineQunatity,
							instruction: "",
						})),
					}));
				}
				setRecentPrescriptions(prescriptions);
				setShowPrescriptionsCard(true);
			} catch (error) {
				console.error("Error fetching recent prescriptions:", error);
				setRecentPrescriptions([]);
				setShowPrescriptionsCard(false);
			} finally {
				setLoading(false);
			}
		},
		[isHospitalConnected, customerType, pharmacyPOS?.hospital_id, setLoading]
	);

	const handleSelectPrescription = useCallback((prescription) => {
		setSelectedPrescription(prescription);
		setShowPrescriptionOffcanvas(true);
	}, []);

	const toggleConsultation = useCallback(() => {
		setConsultationOpen((prev) => !prev);
	}, []);

	const addPrescriptionToCart = useCallback(
		async (prescription) => {
			// console.log("Raw prescription data:", prescription);
			if (!prescription) return;

			setLoading(true);
			try {
				let processed = false;

				// Extract medications array from different possible structures
				let medications = [];

				if (Array.isArray(prescription)) {
					// If prescription is already an array
					medications = prescription;
				} else if (prescription.items && Array.isArray(prescription.items)) {
					// If prescription has items array
					medications = prescription.items;
				} else if (prescription.prescriptions && Array.isArray(prescription.prescriptions)) {
					// If prescription has prescriptions array
					medications = prescription.prescriptions;
				} else if (prescription.medication_name || prescription.salt_composition) {
					// If it's a single medication object (like your example)
					medications = [prescription];
				} else if (prescription.medications && Array.isArray(prescription.medications)) {
					// Another possible structure
					medications = prescription.medications;
				}

				// console.log("Processed medications:", medications);

				for (const medication of medications) {
					// Extract search term - try multiple fields in priority order
					let searchTerm = "";

					if (medication.salt_composition) {
						searchTerm = medication.salt_composition;
					} else if (medication.medication_name) {
						searchTerm = medication.medication_name;
					} else if (medication.medicineName) {
						searchTerm = medication.medicineName;
					} else if (medication.brandName) {
						searchTerm = medication.brandName;
					} else if (medication.name) {
						searchTerm = medication.name;
					} else if (medication.productName) {
						searchTerm = medication.productName;
					}

					if (!searchTerm) {
						console.warn("No search term found for medication:", medication);
						toast.info(t("Could not identify medication"));
						continue;
					}

					// console.log("Searching for:", searchTerm);

					// Search for medicines using the extracted term
					const medicines = await loadMedicines(searchTerm);

					if (!medicines?.length) {
						// Try alternative search if first search fails
						const alternativeSearch = medication.medication_name || medication.medicineName;
						if (alternativeSearch && alternativeSearch !== searchTerm) {
							console.log("Trying alternative search:", alternativeSearch);
							const alternativeMedicines = await loadMedicines(alternativeSearch);

							if (alternativeMedicines?.length) {
								let product = alternativeMedicines[0].raw || alternativeMedicines[0];
								let batches = product.stockData || [];
								
								// Find the first medicine that has stock
								for (const med of alternativeMedicines) {
									const p = med.raw || med;
									const b = p.stockData || [];
									if (b.length > 0) {
										product = p;
										batches = b;
										break;
									}
								}

								if (!batches.length) {
									toast.info(
										t("No stock available for {{name}}", {
											name: medication.medication_name || searchTerm
										})
									);
									continue;
								}

								const batch = batches[0];
								const prescribedQty = parseInt(medication.qty, 10) ||
									parseInt(medication.quantity, 10) ||
									parseInt(medication.dosage_qty, 10) || 1;

								upsertCartItem(product, batch, prescribedQty);
								processed = true;
								continue;
							}
						}

						toast.info(
							t("Product not found: {{name}}", {
								name: medication.medication_name || medication.medicineName || searchTerm
							})
						);
						continue;
					}

					// Found medicines
					let product = medicines[0].raw || medicines[0];
					let batches = product.stockData || [];
					
					// Find the first medicine that has stock
					for (const med of medicines) {
						const p = med.raw || med;
						const b = p.stockData || [];
						if (b.length > 0) {
							product = p;
							batches = b;
							break;
						}
					}

					if (!batches.length) {
						toast.info(
							t("No stock available for {{name}}", {
								name: medication.medication_name || searchTerm
							})
						);
						continue;
					}

					const batch = batches[0];
					const prescribedQty = parseInt(medication.qty, 10) ||
						parseInt(medication.quantity, 10) ||
						parseInt(medication.dosage_qty, 10) || 1;

					upsertCartItem(product, batch, prescribedQty);
					processed = true;
				}

				if (processed) {
					setAddedPrescriptions((prev) => {
						const newSet = new Set(prev);
						if (prescription._id) {
							newSet.add(prescription._id);
						}
						return newSet;
					});
					// toast.success(t("Prescription items added to cart"));
				} else {

					// toast.info(t("No items could be added from the prescription"));
				}
			} catch (error) {
				console.error("Error adding prescription to cart:", error);
				Swal.fire({
					icon: "error",
					title: t("Failed to add prescriptions"),
					text: t("We could not add the prescription items to the cart. Please try again."),
					confirmButtonText: t("OK"),
				});
			} finally {
				setLoading(false);
			}
		},
		[loadMedicines, setLoading, t, upsertCartItem]
	);

	return {
		recentPrescriptions,
		showPrescriptionsCard,
		setShowPrescriptionsCard,
		showPrescriptionOffcanvas,
		setShowPrescriptionOffcanvas,
		selectedPrescription,
		setSelectedPrescription,
		consultationOpen,
		toggleConsultation,
		setConsultationOpen,
		addedPrescriptions,
		setAddedPrescriptions,
		addPrescriptionToCart,
		loadRecentPrescriptions,
		handleSelectPrescription,
		setRecentPrescriptions,
		prescriptionDetails
	};
};

